import { useDrugAutocomplete } from '@/entities/drug/hooks/useDrugQueries';
import { cn } from '@/shared/lib/utils';
import { Search, X } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent } from 'react';

/**
 * 의약품 검색 입력 + 자동완성 (M-10 히어로 · M-11 상단 바 공통)
 * - 두 글자 이상 입력하면 250ms 뒤 자동완성을 연다 (GET /web/drugs/suggestions, 최대 10개)
 * - 엔터 · [검색] · 자동완성 항목 선택 모두 "검색 실행"이다. 상세 드로어를 바로 열지 않는다 (M-15 C)
 */
export const DrugSearchBox = ({
  variant,
  defaultValue = '',
  autoFocus,
  onSubmit,
}: {
  variant: 'hero' | 'bar';
  defaultValue?: string;
  autoFocus?: boolean;
  onSubmit: (keyword: string) => void;
}) => {
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const { data, isFetching, enabled, keyword } = useDrugAutocomplete(value);

  // 디바운스가 끝나기 전(입력값 ≠ 조회 키워드)에는 이전 목록을 보여주지 않는다
  const settled = keyword === value.trim();
  const items = settled && enabled ? (data ?? []) : [];
  const showList = open && value.trim().length >= 2;

  const submit = (keyword: string) => {
    const k = keyword.trim();
    if (!k) {
      inputRef.current?.focus();
      return;
    }
    setValue(k);
    setOpen(false);
    setActive(-1);
    onSubmit(k);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 엔터는 무시
    if (e.key === 'ArrowDown' && items.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp' && items.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submit(active >= 0 && items[active] ? items[active].drugName : value);
    } else if (e.key === 'Escape' && showList) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const hero = variant === 'hero';

  return (
    <div className={cn('relative w-full', hero && 'lg:w-[760px]')}>
      <div
        className={cn(
          'flex items-center bg-surface',
          hero
            ? 'h-[60px] gap-3 rounded-xl border-2 border-ink pr-2.5 pl-[22px]'
            : 'h-[54px] gap-3 rounded-lg border border-line-strong pr-2.5 pl-5',
        )}
      >
        <Search className="size-4 shrink-0 text-ink" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label="의약품 검색"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          placeholder="제품명 · 성분명 · 제약사로 검색"
          className="min-w-0 flex-1 bg-transparent text-[15px] leading-[23px] text-ink outline-none placeholder:text-ink-8 [&::-webkit-search-cancel-button]:hidden"
        />
        {!hero && value && (
          <button
            type="button"
            aria-label="검색어 지우기"
            onClick={() => {
              setValue('');
              inputRef.current?.focus();
            }}
            className="p-1 text-ink-8 hover:text-ink-4"
          >
            <X className="size-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => submit(value)}
          className={cn(
            'shrink-0 bg-ink font-medium text-white hover:bg-ink/85',
            hero
              ? 'h-11 w-[86px] rounded-md text-sm leading-[21px]'
              : 'h-10 w-[74px] rounded-[9px] text-[13px] leading-5',
          )}
        >
          검색
        </button>
      </div>

      {showList && (
        <ul
          id={listId}
          role="listbox"
          // 항목을 누르는 동안 입력 blur로 목록이 먼저 닫히지 않게 한다
          onMouseDown={(e) => e.preventDefault()}
          className="absolute inset-x-0 top-full z-30 mt-2 max-h-[360px] overflow-y-auto rounded-lg border border-line bg-surface py-1.5 shadow-[0_8px_24px_rgb(0_0_0/8%)]"
        >
          {items.length > 0 ? (
            items.map((d, i) => (
              <li
                key={d.drugCode}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => submit(d.drugName)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-4 py-2.5',
                  i === active && 'bg-subtle',
                )}
              >
                <Search className="size-3.5 shrink-0 text-ink-8" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-[13px] leading-5 text-ink-2">
                  <Highlight text={d.drugName} keyword={value.trim()} />
                </span>
                <span className="shrink-0 text-[11px] leading-[17px] text-ink-7">
                  {[d.mainIngredientName, d.pharmaCompanyName]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-[12px] leading-[18px] text-ink-7">
              {!settled || isFetching
                ? '검색 중…'
                : '일치하는 의약품이 없어요. 엔터를 누르면 검색합니다.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

/** 입력한 글자와 겹치는 부분을 굵게 */
const Highlight = ({ text, keyword }: { text: string; keyword: string }) => {
  const i = keyword ? text.indexOf(keyword) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <b className="font-bold text-ink">{text.slice(i, i + keyword.length)}</b>
      {text.slice(i + keyword.length)}
    </>
  );
};
